import { Link, useLocation } from "react-router-dom";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeartOutlined";
import { useEffect, useRef, useState } from "react";

import jiraLogo from "../assets/svgs/jira-logo.svg";
import velocityLogo from "../assets/logo-light-v2.svg";
import velocityLogoSmall from "../assets/svgs/xlr8_logo_small.svg";
import { constants } from "../services/constants/JIRA I_Issue_Collector";
import { useAuth } from "../contexts/AuthContext";
import GlobalDashboardButton from "../components/GlobalDashboardButton";
import {
  NavItem,
  navItems,
  doesRouteMatch,
  findActiveParent,
  findChildByUrl,
  findRouteRequirement,
} from "./navConfig";
import {
  clearFlyoutPosition,
  findOpenFlyoutItem,
  positionFlyout,
} from "./sidebarFlyout";

interface SidebarV2Props {
  user: {
    name: string;
    occupation: string;
  };
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
}

const toMenuId = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const normalizeUrl = (url: string) => (url.startsWith("/") ? url : `/${url}`);

const NARROW_VIEWPORT_QUERY = "(max-width: 640px)";

// The <=640px rail is also expressed as a media query in _sidebarV2.scss so the
// width is correct before hydration, but the rest of the collapsed presentation
// (label fade, logo cross-fade, chevron rotation, flyout submenus) is keyed off
// the `collapsed` class — so the narrow viewport has to set that class too.
const useIsNarrowViewport = () => {
  const [isNarrow, setIsNarrow] = useState(
    () => window.matchMedia?.(NARROW_VIEWPORT_QUERY).matches ?? false,
  );

  useEffect(() => {
    const query = window.matchMedia?.(NARROW_VIEWPORT_QUERY);
    if (!query) return;

    const onChange = (event: MediaQueryListEvent) =>
      setIsNarrow(event.matches);

    setIsNarrow(query.matches);
    query.addEventListener("change", onChange);

    return () => query.removeEventListener("change", onChange);
  }, []);

  return isNarrow;
};

/**
 * AutoXLR8 Theme-2 navy sidebar (DQ-8763).
 *
 * Layout, collapse animation, logo cross-fade and the collapsed flyout
 * submenus are driven entirely by CSS in styles/_sidebarV2.scss — the single
 * `collapsed` class on `.left-panel` is the only state the markup contributes.
 * Nav items, permission guards and route matching come from ./navConfig so this
 * component and the legacy `Sidebar` never diverge.
 */
const SidebarV2 = ({ isSidebarOpen, setIsSidebarOpen }: SidebarV2Props) => {
  const { pathname } = useLocation();
  const { hasPermission, isAdmin } = useAuth();
  const isNarrowViewport = useIsNarrowViewport();
  const isPanelExpanded = isSidebarOpen && !isNarrowViewport;
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(() =>
    findActiveParent(pathname),
  );

  const panelRef = useRef<HTMLElement | null>(null);
  // Which rows the pointer is currently over. A `WeakSet` so an unmounted row
  // is not retained; `:hover` cannot be read back reliably outside a browser.
  const hoveredItems = useRef<WeakSet<HTMLElement>>(new WeakSet());

  useEffect(() => {
    setExpandedMenu(findActiveParent(pathname));
  }, [pathname]);

  // A flyout left open across a window resize keeps the placement measured for
  // the old viewport, so it is recomputed against the new one.
  useEffect(() => {
    const onResize = () => {
      const openItem = findOpenFlyoutItem(panelRef.current);
      if (openItem) positionFlyout(openItem);
    };

    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Mirrors the legacy sidebar's guard so both implementations resolve
  // visibility from the same permission model.
  const hasRoutePermission = (url: string): boolean => {
    if (isAdmin) return true;

    const normalizedUrl = normalizeUrl(url);

    const child = findChildByUrl(normalizedUrl);
    if (child?.requiredAnyPermission && child.requiredAnyPermission.length > 0) {
      return child.requiredAnyPermission.some((p) =>
        hasPermission(p.entity, p.action),
      );
    }

    const requirement = findRouteRequirement(normalizedUrl);
    if (!requirement) return true;

    return hasPermission(requirement.entity, requirement.action);
  };

  const openFeedbackWindow = () => {
    const blankWindow = window.open("Feedback");
    const feedbackDocument = blankWindow?.document;
    if (feedbackDocument) {
      feedbackDocument.open();
      feedbackDocument.write(`
                    <html>
                      <head>
                        <title>Feedback</title>
                      </head>
                      <body>
                        ${constants.JIRA_ISSUE_COLLECTOR_SCRIPT}
                      </body>
                    </html>
                  `);
      feedbackDocument.close();
    }
  };

  const visibleNavItems = navItems.filter((navItem) => {
    const permittedChildren = navItem.children?.filter((child) =>
      hasRoutePermission(child.url),
    );

    return navItem.children
      ? permittedChildren && permittedChildren.length > 0
      : navItem.url
        ? hasRoutePermission(navItem.url)
        : true;
  });

  const placeFlyout = (event: { currentTarget: HTMLLIElement }) => {
    if (isPanelExpanded) return;
    positionFlyout(event.currentTarget);
  };

  const enterFlyout = (event: { currentTarget: HTMLLIElement }) => {
    hoveredItems.current.add(event.currentTarget);
    placeFlyout(event);
  };

  // The CSS reveal is `:hover, :focus-within`, so the panel is still open when
  // only one of the two ends. Releasing the placement then would drop the row
  // back to the unanchored `top: 0` while it is on screen, which is the very
  // overflow this fix removes — each side therefore checks the other first.
  const leaveFlyout = (event: { currentTarget: HTMLLIElement }) => {
    const item = event.currentTarget;
    hoveredItems.current.delete(item);

    if (item.contains(document.activeElement)) return;

    clearFlyoutPosition(item);
  };

  const blurFlyout = (event: {
    currentTarget: HTMLLIElement;
    relatedTarget: EventTarget | null;
  }) => {
    const item = event.currentTarget;
    // `relatedTarget` is the element taking focus; `document.activeElement` is
    // still the outgoing one while focusout is dispatched.
    const keepsFocus =
      event.relatedTarget instanceof Node && item.contains(event.relatedTarget);

    if (keepsFocus || hoveredItems.current.has(item)) return;

    clearFlyoutPosition(item);
  };

  const renderNavItem = (navItem: NavItem) => {
    const { text, url, icon, isStroke, children, routes } = navItem;
    const permittedChildren = children?.filter((child) =>
      hasRoutePermission(child.url),
    );
    const allRoutes = permittedChildren
      ? permittedChildren.flatMap((child) => child.routes || [])
      : routes || [];
    const isActive = allRoutes.some((route) => doesRouteMatch(pathname, route));
    // In collapsed mode the flyout is revealed by CSS :hover, so the expanded
    // class is only meaningful for the inline accordion.
    const isExpanded =
      !!permittedChildren && isPanelExpanded && expandedMenu === text;
    const menuId = toMenuId(text);

    if (!permittedChildren) {
      // A category with neither children nor a route is a non-clickable label,
      // so it renders as a section group header rather than a nav row.
      if (!url) {
        return (
          <li className="nav-section" key={text}>
            <div className="nav-section-title">
              <span className="section-text">{text}</span>
            </div>
          </li>
        );
      }

      return (
        <li className="nav-item" key={text}>
          <Link
            to={normalizeUrl(url)}
            className={`nav-link modern-nav-link ${isActive ? "active" : ""}`}
          >
            <span className="nav-link-content">
              <span
                className={`nav-icon ${isStroke ? "nav-icon-stroke" : ""}`.trim()}
                aria-hidden="true"
              >
                {icon}
              </span>
              <span className="nav-text">{text}</span>
            </span>
          </Link>
        </li>
      );
    }

    return (
      <li
        className="nav-item"
        key={text}
        // The flyout is revealed by CSS, but its vertical anchor depends on
        // where the row sits in the viewport, so it is measured as it opens.
        onMouseEnter={enterFlyout}
        onFocus={placeFlyout}
        onMouseLeave={leaveFlyout}
        onBlur={blurFlyout}
      >
        <button
          type="button"
          id={`${menuId}-trigger`}
          className={`nav-link modern-nav-link expandable-link ${
            isActive ? "active" : ""
          }`}
          aria-expanded={isExpanded}
          aria-controls={`${menuId}-submenu`}
          onClick={() =>
            setExpandedMenu((current) => (current === text ? null : text))
          }
        >
          <span className="nav-link-content">
            <span
              className={`nav-icon ${isStroke ? "nav-icon-stroke" : ""}`.trim()}
              aria-hidden="true"
            >
              {icon}
            </span>
            <span className="nav-text">{text}</span>
            <span className={`nav-caret ${isExpanded ? "expanded" : ""}`}>
              <span className="nav-caret-chevron" aria-hidden="true" />
            </span>
          </span>
        </button>

        <ul
          id={`${menuId}-submenu`}
          className={`nav-submenu ${isExpanded ? "expanded" : ""}`}
          aria-labelledby={`${menuId}-trigger`}
        >
          {permittedChildren.map((subItem) => {
            const isChildActive = (subItem.routes || []).some((route) =>
              doesRouteMatch(pathname, route),
            );

            return (
              <li key={subItem.text}>
                <Link
                  to={subItem.url}
                  className={`nav-link modern-submenu-link ${
                    isChildActive ? "active" : ""
                  }`}
                >
                  <span className="nav-icon" aria-hidden="true">
                    {subItem.icon}
                  </span>
                  <span className="nav-text">{subItem.text}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </li>
    );
  };

  return (
    <aside
      ref={panelRef}
      className={`left-panel ${isPanelExpanded ? "" : "collapsed"}`}
      data-testid="sidebar-v2"
    >
      <div className="sidebar-logo">
        {/* The exact expanded/condensed marks Navbar used to render, moved here
            per the target design. Both variants render and cross-fade via CSS
            opacity so the mark transitions smoothly as the sidebar collapses. */}
        <img
          className="sidebar-logo-full"
          src={velocityLogo}
          alt="Velocity Logo"
        />
        <img
          className="sidebar-logo-small"
          src={velocityLogoSmall}
          alt=""
          aria-hidden="true"
        />

        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => setIsSidebarOpen(!isPanelExpanded)}
          aria-label={isPanelExpanded ? "Collapse sidebar" : "Expand sidebar"}
          aria-expanded={isPanelExpanded}
        >
          <span className="sidebar-toggle-chevron" aria-hidden="true" />
        </button>
      </div>

      <div className="modern-nav-container">
        <nav className="modern-nav" aria-label="Main navigation">
          {visibleNavItems.length === 0 ? (
            <ul className="nav-section-items">
              <li className="nav-item empty-state">
                <span className="nav-link modern-nav-link">
                  <span className="nav-link-content">
                    <span className="nav-text">No Menu Items Available</span>
                  </span>
                </span>
              </li>
            </ul>
          ) : (
            <ul className="nav-section-items">
              {visibleNavItems.map((navItem) => renderNavItem(navItem))}
            </ul>
          )}

          {/* Logout is not duplicated here: it lives in the Navbar user menu,
              which the V2 shell always renders. */}
          <div className="nav-bottom-section">
            {isAdmin && (
              <button
                type="button"
                className="sidebar-bottom-action dashboard-button"
                onClick={() => setIsDashboardOpen(true)}
              >
                <span className="nav-icon" aria-hidden="true">
                  <MonitorHeartIcon />
                </span>
                <span className="nav-text">Grafana Dashboard</span>
              </button>
            )}

            <button
              type="button"
              className="sidebar-bottom-action jira-feedback"
              onClick={openFeedbackWindow}
            >
              <span className="nav-icon" aria-hidden="true">
                <img src={jiraLogo} alt="" />
              </span>
              <span className="nav-text">Jira Feedback</span>
            </button>
          </div>
        </nav>
      </div>

      <GlobalDashboardButton
        isOpen={isDashboardOpen}
        onClose={() => setIsDashboardOpen(false)}
        showFloatingButton={false}
      />
    </aside>
  );
};

export default SidebarV2;
